import { useEffect, useMemo, useRef, useState } from 'react';
import type { AnyEvent, HealthResponse } from '@intentionalist/shared';
import { Replica } from './replica.js';
import { SyncEngine, type SyncState } from './engine.js';
import { Catalog } from './Catalog.js';
import { ReviewSession } from './ReviewSession.js';
import { ReleasePipeline } from './ReleasePipeline.js';
import { InvestmentList } from './InvestmentList.js';
import { Reports } from './Reports.js';
import { Export } from './Export.js';
import { ItemForm } from './ItemForm.js';
import { Settings } from './Settings.js';
import { pendingStartupWrites } from './migrations.js';
import type { StorageDurability } from './storage.js';
import {
  DESTINATIONS,
  LANDING,
  VIEWS,
  urlFor,
  viewFromUrl,
  viewsIn,
  type View,
} from './navigation.js';

/**
 * The application shell: navigation, sync state, and the active destination.
 *
 * Replaces `SyncPanel`, which began as Phase 2 scaffolding and ended up holding
 * every screen in one vertical column with seven peer buttons in the middle.
 * In the default state that column was about five screens tall before the user
 * had done anything, because capture and the catalog were both fully expanded
 * above and below each other.
 *
 * Three things changed. Navigation is four destinations grouped by intent
 * (`navigation.ts`), rendered as a bottom bar on a phone and a side rail on a
 * wide screen -- one information architecture, two layouts, which is what
 * `NFR-COMP-4` asks for, and a bar within thumb reach, which is what
 * `NFR-UX-1` asks for. Capture became an action rather than a place. And the
 * sync readout became a single line in the header rather than a four-row table
 * on one screen -- and now appears only when it has something to report, with
 * the resting state in Diagnostics, so the space above the catalog is spent on
 * the catalog.
 */
export function Shell({
  userId,
  onSignedOut,
  health,
  healthError,
  durability,
  commit,
  engine: injected,
}: {
  userId: string;
  onSignedOut: () => void;
  health: HealthResponse | null;
  healthError: string | null;
  durability: StorageDurability | null;
  commit: string;
  /**
   * The engine, for tests.
   *
   * The default is the real one against the real database. The seam exists
   * because what happens on start -- the replica hydrating, then migrations
   * running against it -- is behavior worth asserting, and it cannot be
   * reached while the engine is built in here.
   */
  engine?: SyncEngine;
}) {
  const engine = useMemo(() => injected ?? new SyncEngine(new Replica()), [injected]);
  const [state, setState] = useState<SyncState>(engine.current());
  /** Bumped on every change, since the manifest is mutated in place. */
  const [version, setVersion] = useState(0);
  const [starting, setStarting] = useState(true);
  const [fault, setFault] = useState<string | null>(null);
  const [view, setView] = useState<View>(() => viewFromUrl(location.search));
  const [capturing, setCapturing] = useState(false);
  /**
   * Whether the catalog is showing one item rather than the list.
   *
   * The shell's anchored action and the item form's save button occupy the same
   * corner of the screen, so only one of them can be there. The catalog owns
   * which item is open; this is the shell being told, rather than the shell
   * reaching in to ask.
   */
  const [onItemPage, setOnItemPage] = useState(false);
  const live = useRef(true);

  const refresh = () => {
    if (live.current) setVersion((n) => n + 1);
  };

  /**
   * Hands events to the engine and returns.
   *
   * Deliberately not awaited at the call site: the edit is durable once the
   * replica has it, and waiting for the push would put the network back on the
   * path that `NFR-PERF-1` measures.
   */
  const write = (events: AnyEvent[]) => void engine.write(events).then(refresh);

  /** Navigates, and keeps the URL in step so back works and links are shareable. */
  const go = (next: View) => {
    setView(next);
    history.pushState({ view: next }, '', urlFor(next));
  };

  useEffect(() => {
    live.current = true;
    const unsubscribe = engine.subscribe((next) => {
      if (!live.current) return;
      setState(next);
      refresh();
      // The session ended mid-sync. Returning to the sign-in form is the only
      // thing that will let the queued work drain.
      if (next.signedOut) onSignedOut();
    });

    engine
      .start(userId)
      .then(async () => {
        if (live.current) setStarting(false);
        refresh();

        /*
         * Migrations and first-run seeding happen after the replica is up, not
         * before. Both are expressed as events against the projection (see
         * `migrations.ts`), so they need the hydrated manifest to decide whether
         * there is anything to do -- and both are idempotent, so running them on
         * every start is safe and is the only way a device that was offline for
         * a release catches up.
         */
        const pending = pendingStartupWrites(engine.replica.manifest);
        if (pending.length > 0 && live.current) {
          await engine.write(pending);
          refresh();
        }
      })
      .catch((error: unknown) => {
        if (!live.current) return;
        setStarting(false);
        setFault(error instanceof Error ? error.message : 'Could not start syncing.');
      });

    return () => {
      live.current = false;
      unsubscribe();
      engine.stop();
    };
    // `engine` is stable for the life of this component, and `userId` identifies
    // whose replica it is -- a change there means a different account.
  }, [engine, userId, onSignedOut]);

  /**
   * The back button, and a tapped notification.
   *
   * The service worker focuses an existing window and posts where to go rather
   * than navigating it: the page owns its routing, and a worker calling
   * `navigate` would discard whatever was half-typed into a form.
   */
  useEffect(() => {
    const onPop = () => setView(viewFromUrl(location.search));
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type === 'navigate' && typeof data.url === 'string') {
        const asked = viewFromUrl(new URL(data.url, location.origin).search);
        setView(asked);
        history.pushState({ view: asked }, '', urlFor(asked));
      }
    };

    addEventListener('popstate', onPop);
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => {
      removeEventListener('popstate', onPop);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, []);

  if (starting) {
    return (
      <p className="muted" role="status">
        preparing your manifest…
      </p>
    );
  }

  const destination = VIEWS[view].destination;
  const siblings = viewsIn(destination);

  return (
    <div className="shell">
      <SyncLine state={state} onSync={() => void engine.syncNow()} />

      {fault !== null && (
        <p className="warn" role="alert">
          {fault}
        </p>
      )}

      {state.superseded.length > 0 && (
        <div className="warn" role="alert">
          <p>
            {state.superseded.length} change{state.superseded.length === 1 ? '' : 's'} you made
            {state.superseded.length === 1 ? ' was' : ' were'} replaced by a newer change from
            another device. The earlier value is still in this item&apos;s history.
          </p>
          <button type="button" onClick={() => void engine.dismissSuperseded()}>
            Dismiss
          </button>
        </div>
      )}

      {/* Only where there is more than one, so the catalog gets no stray tab. */}
      {siblings.length > 1 && (
        <div className="subnav" role="tablist" aria-label={`${destination} views`}>
          {siblings.map((candidate) => (
            <button
              key={candidate}
              type="button"
              role="tab"
              aria-selected={view === candidate}
              className={view === candidate ? 'pressed' : ''}
              onClick={() => go(candidate)}
            >
              {VIEWS[candidate].label}
            </button>
          ))}
        </div>
      )}

      <div className="destination">
        {capturing ? (
          <ItemForm
            manifest={engine.replica.manifest}
            attach={{ db: engine.replica.db, userId }}
            onWrite={write}
            onSave={(events) => {
              write(events);
              setCapturing(false);
            }}
          />
        ) : (
          <Active
            view={view}
            engine={engine}
            version={version}
            userId={userId}
            write={write}
            onSignedOut={onSignedOut}
            health={health}
            healthError={healthError}
            durability={durability}
            commit={commit}
            state={state}
            onEditingChange={setOnItemPage}
            onSync={() => void engine.syncNow()}
            // Leaving a review returns to the catalog, which is where it was
            // started from. The session is stored, so it is still waiting.
            onLeave={() => go(LANDING.catalog)}
          />
        )}
      </div>

      {/*
        The primary action, anchored rather than inline.
        Capture is what someone opens the app to do (goal G1), and on a phone an
        action at the top of a scrolling column is the least reachable thing on
        the screen (NFR-UX-1).
      */}
      {/*
        Not while an item is open: there the anchored action is that item's
        "Save changes", and offering "Add an item" in the same corner would put
        the way out of this item on top of the way to save it.
      */}
      {destination === 'catalog' && !onItemPage && (
        <button
          type="button"
          // Quiet while it says "Cancel": closing the form commits nothing.
          className={capturing ? 'primary-action' : 'primary-action primary'}
          onClick={() => setCapturing((open) => !open)}
        >
          {capturing ? 'Cancel' : 'Add an item'}
        </button>
      )}

      <nav className="destinations" aria-label="Sections">
        {DESTINATIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-current={destination === option.id ? 'page' : undefined}
            className={destination === option.id ? 'pressed' : ''}
            onClick={() => {
              setCapturing(false);
              go(LANDING[option.id]);
            }}
          >
            {option.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

/**
 * The sync readout, when there is something to say (FR-10.4).
 *
 * It used to be a permanent line at the top of every screen, and what it said
 * almost always was "synced" -- a word confirming that nothing had happened,
 * above a button for making nothing happen again, on the screen someone opened
 * to look at their things. That is a header's worth of space spent on the state
 * that needs no attention.
 *
 * So it now appears only when it has news: changes not yet pushed, or a sync
 * that failed. That is the state risk R4 is about -- until they are pushed those
 * events exist on this device and nowhere else -- and keeping the line for that
 * case alone makes it mean something when it is there. The resting state, what
 * it last did and how many photos are queued, is in Diagnostics, along with
 * `Sync now` for anyone who wants it on demand (FR-10.7).
 */
function SyncLine({ state, onSync }: { state: SyncState; onSync: () => void }) {
  const pending = state.pending;
  const quiet = pending === 0 && state.error === null;

  return (
    /*
     * The region stays in the document even when it has nothing in it.
     *
     * A live region that is inserted already holding its message is not
     * reliably announced -- assistive technology watches regions it already
     * knows about for changes. Mounting it empty and filling it later is what
     * makes the warning reach someone who is not looking at the screen. Empty,
     * it is an unstyled div and takes no height, which is the whole point.
     */
    <div aria-live="polite">
      {quiet ? null : (
        <div className="syncline">
          <span>
            {state.error !== null ? (
              <span className="warn">{state.error}</span>
            ) : (
              <strong>
                {pending} change{pending === 1 ? '' : 's'} waiting
              </strong>
            )}
          </span>
          <button type="button" onClick={onSync}>
            Sync now
          </button>
        </div>
      )}
    </div>
  );
}

function Active({
  view,
  engine,
  version,
  userId,
  write,
  onSignedOut,
  health,
  healthError,
  durability,
  commit,
  state,
  onSync,
  onLeave,
  onEditingChange,
}: {
  view: View;
  engine: SyncEngine;
  version: number;
  userId: string;
  write: (events: AnyEvent[]) => void;
  onSignedOut: () => void;
  health: HealthResponse | null;
  healthError: string | null;
  durability: StorageDurability | null;
  commit: string;
  state: SyncState;
  onSync: () => void;
  onLeave: () => void;
  /** Passed through to the catalog, which is the only view with an item page. */
  onEditingChange: (editing: boolean) => void;
}) {
  const manifest = engine.replica.manifest;

  switch (view) {
    case 'review':
      return (
        <ReviewSession
          replica={engine.replica}
          manifest={manifest}
          version={version}
          index={engine.replica.index}
          onWrite={write}
          onLeave={onLeave}
        />
      );
    case 'release':
      return <ReleasePipeline manifest={manifest} version={version} onWrite={write} />;
    case 'invest':
      return <InvestmentList manifest={manifest} version={version} onWrite={write} />;
    case 'reports':
      return <Reports manifest={manifest} version={version} replica={engine.replica} />;
    case 'export':
      return <Export manifest={manifest} version={version} replica={engine.replica} />;
    case 'account':
    case 'appearance':
    case 'reminders':
    case 'organize':
    case 'diagnostics':
      return (
        <Settings
          view={view}
          manifest={manifest}
          version={version}
          onWrite={write}
          userId={userId}
          onSignOut={onSignedOut}
          sync={state}
          health={health}
          healthError={healthError}
          durability={durability}
          commit={commit}
          onSync={onSync}
        />
      );
    default:
      return (
        <Catalog
          manifest={manifest}
          version={version}
          index={engine.replica.index}
          db={engine.replica.db}
          userId={userId}
          onWrite={write}
          onEditingChange={onEditingChange}
        />
      );
  }
}
